import { useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, History, MapPin, TriangleAlert } from 'lucide-react';
import type { StockUsageRow, Unit } from '@eombor/contracts';
import { Button, Card, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useMyStorerooms } from '../../subdivision/model/subdivision.queries.js';
import { useUsages, useWorkers } from '../model/storekeeping.queries.js';
import { UsageDetailDialog } from './UsageDetailDialog.js';

const pad = (n: number): string => String(n).padStart(2, '0');
const dayKey = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const timeOf = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const qtyText = (qty: number): string => (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

/** Ism bosh harflari — avatar o'rnida: odam ro'yxatni YUZ bo'yicha ko'zdan kechiradi. */
export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/**
 * ISHLATISH TARIXI — «kim, qachon, nechta va nima uchun oldi».
 *
 * Kun bo'yicha guruhlangan lenta, jadval emas: mas'ul bu yerga «bugun kim
 * nima oldi?» degan savol bilan keladi va javob sana sarlavhasi ostida
 * turadi. Har yozuvda ISM birinchi, miqdor ikkinchi, izoh esa iqtibos
 * bo'lib — u xodimning o'z so'zlari.
 */
export const UsageHistoryView = ({ onOpenRequisition }: { onOpenRequisition?: (id: string) => void }) => {
  const [storeroomId, setStoreroomId] = useState<string | undefined>(undefined);
  const [workerId, setWorkerId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  /* Ochilgan yozuv — lentaning O'ZIDA saqlanadi: har kartochka o'z
     oynasini olib yursa, ochilmagan o'nlab oyna daraxtda turaverardi. */
  const [opened, setOpened] = useState<StockUsageRow | null>(null);
  const { canAnywhere } = usePermission();
  const rooms = useMyStorerooms();
  const workers = useWorkers(canAnywhere('storeroom.manage'));
  const usages = useUsages({
    page,
    ...(storeroomId === undefined ? {} : { storeroomId }),
    ...(workerId === undefined ? {} : { workerId }),
  });

  const filterClass = cn(
    'h-9 rounded-[var(--radius-md)] border border-border bg-surface px-3 text-sm text-text',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
  );

  const groups = new Map<string, StockUsageRow[]>();
  for (const row of usages.data?.items ?? []) {
    const key = dayKey(row.usedAt);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const today = dayKey(new Date().toISOString());
  const pages = Math.max(1, Math.ceil((usages.data?.total ?? 0) / (usages.data?.limit ?? 30)));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t('usage.filterRoom')}
          className={filterClass}
          value={storeroomId ?? ''}
          onChange={(event) => {
            setStoreroomId(event.target.value === '' ? undefined : event.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('usage.allRooms')}</option>
          {(rooms.data ?? []).map((room) => (
            <option key={room.id} value={room.id}>
              {room.name}
            </option>
          ))}
        </select>
        {workers.data === undefined ? null : (
          <select
            aria-label={t('usage.filterWorker')}
            className={filterClass}
            value={workerId ?? ''}
            onChange={(event) => {
              setWorkerId(event.target.value === '' ? undefined : event.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('usage.allWorkers')}</option>
            {workers.data.map((worker) => (
              <option key={worker.id} value={worker.id}>
                {worker.fullName}
              </option>
            ))}
          </select>
        )}
      </div>

      {usages.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : usages.isError ? (
        <Card>
          <EmptyState icon={<TriangleAlert size={20} aria-hidden />} title={t('myStock.loadError')} description={usages.error.message} />
        </Card>
      ) : usages.data.items.length === 0 ? (
        <Card>
          <EmptyState icon={<History size={20} aria-hidden />} title={t('usage.empty')} description={t('usage.emptyHint')} />
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {[...groups.entries()].map(([day, rows]) => (
            <section key={day} aria-label={day} className="flex flex-col gap-2">
              <h3 className="tabular sticky top-[env(safe-area-inset-top,0px)] z-[1] w-fit rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold tracking-wide text-text-muted uppercase">
                {day === today ? t('usage.today') : day}
              </h3>
              <ol className="relative flex flex-col gap-2 border-l border-border pl-5">
                {rows.map((row) => (
                  <UsageItem key={row.id} row={row} onOpen={() => setOpened(row)} />
                ))}
              </ol>
            </section>
          ))}

          {pages <= 1 ? null : (
            <div className="flex items-center justify-end gap-2">
              <span className="tabular mr-auto text-xs text-text-muted">
                {page} / {pages}
              </span>
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={14} aria-hidden />
                {t('usage.prev')}
              </Button>
              <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                {t('usage.next')}
                <ChevronRight size={14} aria-hidden />
              </Button>
            </div>
          )}
        </div>
      )}

      {opened === null ? null : (
        <UsageDetailDialog
          row={opened}
          onClose={() => setOpened(null)}
          {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })}
        />
      )}
    </div>
  );
};

/**
 * Lentadagi bitta yozuv — butunlay BOSILADIGAN.
 *
 * Ilgari kartochka ichida talabnoma raqami alohida tugma bo'lib turardi
 * va kartochkaning o'zi bosilmasdi. Endi kartochka bitta nishon: ichida
 * ikkinchi bosiladigan element bo'lsa, u tugma ichidagi tugma bo'lib
 * qolardi — bunday tuzilma HTML da noto'g'ri va klaviatura bilan
 * yuruvchi odamni chalg'itadi. Raqamning o'zi matn bo'lib qoldi,
 * havolasi esa oynaning ichida.
 */
const UsageItem = ({ row, onOpen }: { row: StockUsageRow; onOpen: () => void }) => (
  <li className="relative">
    <span aria-hidden className="absolute top-4 -left-[1.6rem] z-[1] size-2.5 rounded-full border-2 border-surface bg-warn" />
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('usage.detailOpen')}
      className={cn(
        'w-full rounded-[var(--radius-md)] border border-border bg-surface p-3 text-left',
        'shadow-[var(--shadow-1)] transition-colors duration-[var(--motion-fast)]',
        'ease-[var(--ease-standard)] hover:border-border-strong hover:bg-surface-2',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
        >
          {initialsOf(row.workerName)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-text">
            <span className="font-semibold">{row.workerName}</span>
            <span className="tabular font-semibold text-warn">
              −{t('usage.took', { qty: qtyText(row.qty), unit: unitText(row.unit as Unit) })}
            </span>
            <span className="tabular ml-auto text-xs text-text-muted">{timeOf(row.usedAt)}</span>
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-medium text-text">{row.nomenclatureName}</span>
            <span className="rounded-[var(--radius-sm)] border border-border bg-surface-2 px-1.5 font-mono text-[0.6875rem] text-text-muted">
              {row.code}
            </span>
          </p>
          {/* Izoh lentada BIR QATOR: uzun izoh o'nta yozuvni ekrandan
              surib yuborardi, to'lig'i esa oynada turadi. */}
          <p className="truncate border-l-2 border-accent/40 pl-2 text-sm text-text-muted italic">
            {row.comment}
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} aria-hidden />
              {row.zoneName === null ? row.storeroomName : `${row.storeroomName} · ${row.zoneName}`}
            </span>
            {row.requisitionDocNo === null ? null : (
              <span className="inline-flex items-center gap-1">
                <FileText size={12} aria-hidden />
                {row.requisitionDocNo}
              </span>
            )}
          </p>
        </div>
      </div>
    </button>
  </li>
);
